import { Component, Element, Event, EventEmitter, Host, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import debounce from 'debounce';
import {
  StickyToolbarItemNotification,
  handleItemNotification,
  setSessionStorageCookie,
} from '../../../../global/utility/sticky-toolbar/sticky-toolbar-helpers';
import { isMagnoliaEditingMode } from '../../../../helper';

export interface UiCmsStickyToolbarItemSocialFollowLinksBody {
  headline: string;
  leadtext: string;
  mouseover: string;
  iconPath: string;
  notification?: StickyToolbarItemNotification;
  iconActionAriaLabel: string;
}

@Component({
  tag: 'ui-cms-sticky-toolbar-item-standard',
  styleUrls: {
    default: 'ui-cms-sticky-toolbar-item-standard.scss',
    editing: 'ui-cms-sticky-toolbar-item-standard.magnolia.scss',
  },
  shadow: true,
})
export class UiCmsStickyToolbarItemStandard {
  @Element() el: HTMLUiCmsStickyToolbarItemStandardElement;

  @Prop({ mutable: true }) body: UiCmsStickyToolbarItemSocialFollowLinksBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ reflect: true, mutable: true }) toolBarTopPosition: number;

  @Prop({ mutable: true }) item: HTMLElement;

  @Prop({ reflect: true, mutable: true }) open = false;

  @Prop() minHeight = 200;

  @State() notificationIsActive: boolean;

  @Event() itemInteraction: EventEmitter;

  @Event() itemClick: EventEmitter;

  @Event() itemFocusOut: EventEmitter;

  repositionBottomMargin = 10;

  content: HTMLElement;

  toolbarItem: HTMLButtonElement;

  notificationCount = 0;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Listen('notificationStart')
  onNotificationStart() {
    this.open = true;
    this.notificationIsActive = true;
    this.notificationCount += 1;
  }

  @Listen('notificationStop')
  onNotificationStop() {
    this.hideContent();
    this.notificationIsActive = false;
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    if (this.body.notification?.id) {
      handleItemNotification(this.el, this.body.notification);
    }
  }

  componentDidLoad() {
    if (isMagnoliaEditingMode()) {
      const setContentMaxHeightDebounce = debounce(this.setContentMaxHeight.bind(this), 200);
      window.addEventListener('resize', setContentMaxHeightDebounce);
    }
    if (this.toolbarItem) this.setAriaAttributes();
  }

  calculateContentTopPosition(toolbarTopPosition) {
    const toolbarTopDelta = this.el.getBoundingClientRect().top - toolbarTopPosition;
    const totalContentHeight = toolbarTopPosition + this.content.offsetHeight + this.repositionBottomMargin;
    if (totalContentHeight > window.innerHeight) {
      // calculate required content top offset and give it some space to the bottom
      let top = totalContentHeight - window.innerHeight;
      // don't overflow content over top viewport
      if (toolbarTopPosition - top < 0) {
        top = toolbarTopPosition;
      }

      return isMagnoliaEditingMode() ? toolbarTopDelta : -1 * top;
    }
    return isMagnoliaEditingMode() ? toolbarTopDelta : -1.5;
  }

  @Method()
  async showContent(toolbarTopPosition) {
    this.content.style.display = 'block';
    this.open = true;
    this.content.style.top = `${this.calculateContentTopPosition(toolbarTopPosition)}px`;
    if (isMagnoliaEditingMode()) {
      this.setContentMaxHeight();
    }
    this.setAriaAttributes();
  }

  @Method()
  async hideContent() {
    this.open = false;
    this.content.style.display = 'none';
    document.removeEventListener('click', this.hideContent.bind(this));
    this.setAriaAttributes();
  }

  onItemClick() {
    this.itemClick.emit({ el: this.el });
  }

  handleLink(element: any) {
    const linkElement = element.localName === 'sd-link' ? element : element.shadowRoot.children[1];
    if (linkElement.href) {
      window.open(linkElement.href, linkElement.target);
    }
  }

  onClick(e) {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Icon via Sticky Toolbar',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: this.body?.headline,
      },
      category: {
        area: 'Sticky Toolbar',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);

    this.handleEvent(e);
    document.addEventListener('click', this.onItemClick.bind(this));

    requestAnimationFrame(() => {
      this.content?.focus();
    });
  }

  handleEvent(e) {
    e.preventDefault();
    e.stopPropagation();
    this.itemInteraction.emit({ el: this.el });
  }

  setContentMaxHeight() {
    const maxContentHeight = window.innerHeight - this.el.getBoundingClientRect().top;
    this.content.style.maxHeight = `${maxContentHeight - this.repositionBottomMargin}px`;
  }

  handleNotificationCloseClick() {
    this.hideContent();
    setSessionStorageCookie(this.body.notification?.id);
    this.notificationIsActive = false;
    this.notificationCount = 2;
  }

  private setAriaAttributes() {
    (this.toolbarItem as any).updateComplete.then(() => {
      const shadowButton = this.toolbarItem?.shadowRoot?.querySelector('button');
      shadowButton.setAttribute('aria-expanded', `${this.open}`);
    });
  }

  render() {
    return (
      <Host
        class={{
          notify: this.notificationIsActive,
        }}
      >
        <sd-button
          class={{
            open: this.open,
            'toolbar-item': true,
          }}
          ref={(el) => {
            this.toolbarItem = el;
          }}
          onClick={(e) => {
            this.onClick(e);
          }}
          onFocus={() => {
            this.itemFocusOut.emit({ el: this.el });
          }}
        >
          <span class="sr-only">{this.body.iconActionAriaLabel}</span>
          <sd-icon
            library={window.ui.cmsState.iconLibrary}
            name={this.body.iconPath}
            color={this.open ? 'currentColor' : 'white'}
          ></sd-icon>
        </sd-button>
        <div
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          class="content"
          ref={(el) => {
            this.content = el;
          }}
          style={{ minHeight: `${this.minHeight}px` }}
          onFocusout={() => {
            requestAnimationFrame(() => {
              if (!this.el.contains(document.activeElement)) {
                this.itemFocusOut.emit({ el: this.el });
              }
            });
          }}
        >
          <h2 class="sd-headline sd-headline--size-3xl">
            {this.notificationIsActive && this.body.notification?.headline
              ? this.body.notification.headline
              : this.body.headline}
          </h2>
          <sd-button
            class={{
              'content__close-button': true,
              notify: this.notificationIsActive,
            }}
            variant="tertiary"
            onClick={this.handleNotificationCloseClick.bind(this)}
          >
            <span class="sr-only">Schließen</span>
            <sd-icon library={window.ui.cmsState.iconLibrary} name="union-investment/system/close"></sd-icon>
          </sd-button>
          <p
            innerHTML={
              this.notificationIsActive && this.body.notification?.leadtext
                ? this.body.notification.leadtext
                : this.body.leadtext
            }
            class="sd-paragraph"
          ></p>
          <div
            class={{
              'content--slot': true,
            }}
          >
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
